import { useWeather } from '../hooks/useWeather';
import './WeatherPanel.css';

interface WeatherPanelProps {
  lat: number;
  lon: number;
}

const WEATHER_ICONS: Record<string, string> = {
  '01d': '☀️', '01n': '🌙',
  '02d': '⛅', '02n': '☁️',
  '03d': '☁️', '03n': '☁️',
  '04d': '☁️', '04n': '☁️',
  '09d': '🌧️', '09n': '🌧️',
  '10d': '🌦️', '10n': '🌧️',
  '11d': '⛈️', '11n': '⛈️',
  '13d': '🌨️', '13n': '🌨️',
  '50d': '🌫️', '50n': '🌫️',
};

export function WeatherPanel({ lat, lon }: WeatherPanelProps) {
  const { weather, loading, error, lastUpdated } = useWeather(lat, lon, 600000);

  if (loading && !weather) {
    return (
      <div className="weather-panel">
        <div className="weather-loading">Loading weather data...</div>
      </div>
    );
  }

  if (error && !weather) {
    return (
      <div className="weather-panel">
        <div className="weather-error">
          Weather unavailable. Using estimates.
        </div>
      </div>
    );
  }

  if (!weather) return null;

  const icon = WEATHER_ICONS[weather.current.icon] || '🌤️';

  return (
    <div className="weather-panel">
      <div className="weather-header">
        <h3>Current Weather</h3>
        {weather.cached && (
          <span className="weather-cached" title="Cached data">cached</span>
        )}
      </div>
      
      <div className="weather-current">
        <div className="weather-icon">{icon}</div>
        <div className="weather-temp">
          {Math.round(weather.current.temperature)}°C
        </div>
        <div className="weather-desc">{weather.current.description}</div>
      </div>
      
      <div className="weather-details">
        <div className="weather-detail">
          <span className="detail-label">Cloud Cover</span>
          <span className="detail-value">{weather.current.cloud_cover}%</span>
          <div className="detail-bar">
            <div 
              className="detail-bar-fill cloud"
              style={{ width: `${weather.current.cloud_cover}%` }}
            />
          </div>
        </div>
        
        <div className="weather-detail">
          <span className="detail-label">Humidity</span>
          <span className="detail-value">{weather.current.humidity}%</span>
        </div>
        
        <div className="weather-detail">
          <span className="detail-label">Wind</span>
          <span className="detail-value">{weather.current.wind_speed.toFixed(1)} m/s</span>
        </div>
      </div>

      {weather.sun_times && (
        <div className="sun-times">
          <div className="sun-time">
            <span className="sun-icon">🌅</span>
            <span className="sun-label">Sunrise</span>
            <span className="sun-value">
              {new Date(weather.sun_times.sunrise).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="sun-time">
            <span className="sun-icon">🌇</span>
            <span className="sun-label">Sunset</span>
            <span className="sun-value">
              {new Date(weather.sun_times.sunset).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="daylight-hours">
            {weather.sun_times.day_length_hours.toFixed(1)} hours of daylight
          </div>
        </div>
      )}

      <div className="weather-forecast">
        <h4>Next Hours</h4>
        <div className="forecast-list">
          {weather.hourly.slice(0, 6).map((hour, i) => {
            const time = new Date(hour.timestamp);
            const hourIcon = WEATHER_ICONS[hour.icon] || '🌤️';
            return (
              <div key={i} className="forecast-item">
                <span className="forecast-time">
                  {time.getHours().toString().padStart(2, '0')}:00
                </span>
                <span className="forecast-icon">{hourIcon}</span>
                <span className="forecast-temp">{Math.round(hour.temperature)}°</span>
                <span className="forecast-cloud">{hour.cloud_cover}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {lastUpdated && (
        <div className="weather-updated">
          Updated: {lastUpdated.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

